import { z } from 'zod';

import { CartesianPose } from '@sb/geometry';
import { CollisionBoxConfig } from '@sb/integrations/types/CollisionBox';

import {
  ROBOTIQ_FT_300_DEFAULT_COLLISION_BOX,
  ROBOTIQ_FT_300_DEFAULT_TOOLTIP_TRANSFORM,
} from '../constants';

export const RobotiqFT300State = z.object({
  kind: z.literal('RobotiqFT300'),
  isConnected: z.boolean().default(false),
  isBusy: z.boolean().default(false),
  error: z.string().optional(),

  // Force in Newtons
  forceX: z.number().default(0),
  forceY: z.number().default(0),
  forceZ: z.number().default(0),
  // Computed magnitude (XYZ resultant) in Newtons
  forceMagnitude: z.number().default(0),

  // Torque in Newton-meters
  torqueX: z.number().default(0),
  torqueY: z.number().default(0),
  torqueZ: z.number().default(0),

  /**
   * Whether the driver is subtracting a captured offset. The FT-300 has no
   * hardware tare register, so this is software state owned by
   * `arm-control-bot` and reported back — not something the UI can infer.
   */
  isZeroed: z.boolean().default(false),

  tcpTransform: CartesianPose.default(ROBOTIQ_FT_300_DEFAULT_TOOLTIP_TRANSFORM),
});

export type RobotiqFT300State = z.infer<typeof RobotiqFT300State>;

/**
 * How arm-control-bot reaches the sensor.
 *
 * The FT-300 has no Ethernet: it is Modbus RTU over RS-485, and Robotiq ships
 * it with an ACC-ADT-RS485-USB adapter that enumerates on the control box as
 * an FTDI `/dev/ttyUSB*`. That is the only wiring supported today, so this is
 * a plain object rather than a union — reaching the sensor over the
 * end-effector RS-485 bus or through a Modbus/TCP gateway would each need
 * their own transport in arm-control-bot.
 */
export const RobotiqFT300Connection = z.object({
  // Left empty, arm-control-bot finds the USB/RS-485 adapter by its USB
  // descriptors, so there is nothing for an operator to configure and nothing
  // to re-enter when a replug moves the tty index. A path set through the API
  // overrides that, which is the escape hatch for a box carrying two adapters.
  path: z.string().default(''),
});

export type RobotiqFT300Connection = z.infer<typeof RobotiqFT300Connection>;

export const RobotiqFT300Configuration = z.object({
  kind: z.literal('RobotiqFT300'),
  name: z.string().default('Robotiq FT-300'),
  connectionOptions: RobotiqFT300Connection,
  tcpTransform: CartesianPose.default(ROBOTIQ_FT_300_DEFAULT_TOOLTIP_TRANSFORM),
  collisionBox: CollisionBoxConfig.default(
    ROBOTIQ_FT_300_DEFAULT_COLLISION_BOX,
  ),
});

export type RobotiqFT300Configuration = z.infer<
  typeof RobotiqFT300Configuration
>;

export const ROBOTIQ_FT_300_CONFIGURATION_DEFAULT: RobotiqFT300Configuration = {
  kind: 'RobotiqFT300',
  name: 'Robotiq FT-300',
  connectionOptions: { path: '' },
  tcpTransform: ROBOTIQ_FT_300_DEFAULT_TOOLTIP_TRANSFORM,
  collisionBox: ROBOTIQ_FT_300_DEFAULT_COLLISION_BOX,
};

export const RobotiqFT300DirectCommands = z.union([
  z.literal('zero'),
  z.literal('unzero'),
]);

export type RobotiqFT300DirectCommands = z.infer<
  typeof RobotiqFT300DirectCommands
>;

export const RobotiqFT300Command = z.object({
  kind: z.literal('RobotiqFT300Command'),
  command: RobotiqFT300DirectCommands,
});

export type RobotiqFT300Command = z.infer<typeof RobotiqFT300Command>;
